import { useConnection } from '@core/hooks/useConnection';
import { m, AnimatePresence } from 'framer-motion';
import { WifiOff } from 'lucide-react';
import React, { useState } from 'react';
import { Outlet } from 'react-router-dom';

import Navbar from './Navbar';
import KioskSettings from '../../../modules/withdrawal/components/KioskSettings';
import TerminalHeader from '../../../modules/withdrawal/components/TerminalHeader';

import { useAuth } from '@core/context/AuthContext';
import { useUI } from '@core/context/UIContext';


/**
 * MainLayout — App shell with top navigation bar.
 */
const MainLayout = () => {
    const { user, isTerminalMode, deactivateTerminalMode } = useAuth();
    const { showUnlockModal, setShowUnlockModal, terminalBranding, terminalStep, terminalMode } = useUI();
    const [unlockPassword, setUnlockPassword] = useState('');
    const isOnline = useConnection();
    const { setIsScrolled } = useUI();

    const handleScroll = (e) => {
        const scrollTop = e.target.scrollTop;
        if (setIsScrolled) setIsScrolled(scrollTop > 80);
    };

    return (
        <div className="flex flex-col h-screen bg-slate-50 overflow-hidden relative">

            {/* ── OFFLINE BANNER ── */}
            <AnimatePresence>
                {!isOnline && (
                    <m.div
                        initial={{ y: -100 }}
                        animate={{ y: 0 }}
                        exit={{ y: -100 }}
                        className="absolute top-0 left-0 right-0 z-[100] bg-orange-500 text-white py-3 px-6 flex items-center justify-center gap-3 font-black text-[10px] uppercase tracking-[0.2em] shadow-2xl"
                    >
                        <WifiOff size={16} />
                        Offline Mode — Changes will synchronize once Wi-Fi is restored
                    </m.div>
                )}
            </AnimatePresence>

            {/* ── TOP NAV BAR (Universal) ── */}
            <Navbar />

            {/* ── MAIN CONTENT ── */}
            <main 
                onScroll={handleScroll}
                className={`flex-1 overflow-hidden min-h-0 flex flex-col ${window.location.pathname === '/terminal' ? 'p-0' : 'px-0 pt-0 md:pt-4 pb-0'}`}
            >
                <div className="w-full flex-1 flex flex-col min-h-0">
                    <Outlet />
                </div>
            </main>

            {/* ── GLOBAL KIOSK SECURITY ── */}
            {isTerminalMode && (
                <KioskSettings
                    user={user}
                    isTerminalMode={isTerminalMode}
                    showUnlockModal={showUnlockModal}
                    onCloseUnlockModal={() => { setShowUnlockModal(false); setUnlockPassword(''); }}
                    unlockPassword={unlockPassword}
                    setUnlockPassword={setUnlockPassword}
                    onConfirmUnlock={async () => {
                        const success = await deactivateTerminalMode(unlockPassword);
                        if (success) {
                            setShowUnlockModal(false);
                            setUnlockPassword('');
                        }
                    }}
                />
            )}
        </div>
    );
};

export default MainLayout;
